import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRestaurantAuth } from '../contexts/RestaurantAuthContext';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import API_BASE_URL from '../config/api';
import TurnstileWidget from '../components/TurnstileWidget';
import namkeLogoHeader from '../assets/namke-logo-header.png';

const inputClass =
  'w-full min-h-[56px] px-6 bg-namke-cream/60 border border-namke-black/10 rounded-full focus:outline-none focus:ring-2 focus:ring-namke-darkGreen/40 focus:border-transparent transition-colors text-namke-black placeholder-namke-black/40 text-base';

export default function RestaurantLogin() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const cooldownRef = useRef(null);

  const { login, loginWith2FA } = useRestaurantAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const msg = location.state?.message;
    if (msg) {
      setSuccessMessage(msg);
      navigate('/business/login', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const startCooldown = (seconds = 60) => {
    setResendCooldown(seconds);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(email, password, turnstileToken);
      if (result.requires_2fa) {
        setTempToken(result.temp_token || '');
        setMaskedEmail(result.masked_email || `***@${email.split('@')[1] || '***'}`);
        setShow2FA(true);
        startCooldown(60);
      } else if (result.success) {
        navigate('/management', { replace: true });
      } else {
        setError(result.error || t('restaurantAuth.invalidCredentials'));
      }
    } catch (err) {
      setError(t('restaurantAuth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 4) { setError(t('restaurantAuth.enterEmailCode')); return; }
    setLoading(true);
    setError('');
    try {
      const result = await loginWith2FA(tempToken, otpCode);
      if (result.success) {
        navigate('/management', { replace: true });
      } else {
        setError(result.error || t('restaurantAuth.invalidOrExpiredCode'));
      }
    } catch (err) {
      setError(err.message || t('restaurantAuth.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await fetch(`${API_BASE_URL}/auth/2fa/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tempToken}` },
        body: JSON.stringify({}),
      });
      startCooldown(60);
    } catch {
      setError(t('restaurantAuth.cannotResendCode'));
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', background: '#FCFBF5' }}>
      {/* Colonne formulaire */}
      <div style={{ padding: 'clamp(24px, 4vw, 56px) clamp(20px, 5vw, 72px)', display: 'flex', flexDirection: 'column', gap: 'clamp(22px, 3vw, 36px)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
          <img src={namkeLogoHeader} alt="namke" style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
        </Link>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '26px', maxWidth: '440px', width: '100%', margin: '0 auto' }}>
          {!show2FA ? (
            <>
              <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23' }}>Espace cuisinier</span>
                <h1 style={{ margin: 0, fontWeight: 900, fontSize: 'clamp(32px, 3.8vw, 50px)', lineHeight: 1, letterSpacing: '-0.025em' }}>{t('auth.welcomeBack')}</h1>
                <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.6, color: 'rgba(5,16,4,0.74)' }}>{t('restaurantAuth.loginSubtitle')}</p>
              </div>

              {/* Bascule pro / client */}
              <div className="animate-fade-in-delay" style={{ display: 'flex', gap: '4px', background: '#FBF9D9', borderRadius: '999px', padding: '5px', alignSelf: 'flex-start' }}>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.66)', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', padding: '11px 24px', borderRadius: '999px' }}
                >
                  Je commande
                </button>
                <span style={{ background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', padding: '11px 24px', borderRadius: '999px' }}>
                  Je cuisine
                </span>
              </div>

              {successMessage && <div className="form-message form-message--success text-center">{successMessage}</div>}
              {error && <div className="form-message form-message--error text-center">{error}</div>}

              <form onSubmit={handleSubmit} className="animate-fade-in-delay-2" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>E-mail</span>
                  <input
                    type="email"
                    className={inputClass}
                    placeholder="contact@restaurant.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Mot de passe</span>
                  <span className="flex items-center gap-3" style={{ border: '1px solid rgba(5,16,4,0.1)', background: 'rgba(251,249,217,0.6)', borderRadius: '999px', padding: '0 6px 0 24px', minHeight: '56px' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '16px', color: '#051004', outline: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ border: 'none', cursor: 'pointer', background: '#FCFBF5', color: '#054625', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, minHeight: '40px', padding: '0 16px', borderRadius: '999px', whiteSpace: 'nowrap' }}
                    >
                      {showPassword ? 'Masquer' : 'Afficher'}
                    </button>
                  </span>
                </label>

                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer" style={{ fontSize: '14.5px', minHeight: '44px' }}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#054625' }}
                    />
                    {t('auth.rememberMe')}
                  </label>
                  <Link to="/forgot-password" style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: '#EF4C23' }}>
                    {t('auth.forgotPassword')}
                  </Link>
                </div>

                <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />

                <button
                  type="submit"
                  disabled={loading || (!!process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken)}
                  style={{
                    border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5',
                    fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '58px', borderRadius: '999px', opacity: loading ? 0.7 : 1, transition: 'transform 150ms ease',
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px) scale(1.01)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                >
                  {loading ? t('auth.connecting') : t('auth.login')}
                </button>
              </form>

              <span style={{ textAlign: 'center', fontSize: '15px', color: 'rgba(5,16,4,0.74)' }}>
                {t('restaurantAuth.noAccount')}{' '}
                <Link to="/business/signup" style={{ fontWeight: 600 }}>{t('restaurantAuth.registerRestaurant')}</Link>
              </span>
              <span style={{ textAlign: 'center', fontSize: '13.5px', color: 'rgba(5,16,4,0.55)' }}>
                {t('restaurantAuth.areYouClient')}{' '}
                <Link to="/login" style={{ fontWeight: 600, color: '#054625' }}>{t('restaurantAuth.clientLogin')}</Link>
              </span>
            </>
          ) : (
            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
              <div className="text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={40} style={{ color: '#054625' }} />
                <h1 style={{ margin: 0, fontWeight: 900, fontSize: 'clamp(26px, 3vw, 36px)', letterSpacing: '-0.02em' }}>{t('restaurantAuth.twoFactorTitle')}</h1>
                <p style={{ margin: 0, fontSize: '14.5px', color: 'rgba(5,16,4,0.7)' }}>
                  {t('restaurantAuth.codeSentTo')} <strong style={{ color: '#054625' }}>{maskedEmail}</strong>
                </p>
              </div>

              {error && <div className="form-message form-message--error text-center">{error}</div>}

              <form onSubmit={handleOtpSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className={inputClass}
                  style={{ textAlign: 'center', letterSpacing: '0.3em', fontSize: '22px', fontWeight: 700 }}
                  placeholder={t('restaurantAuth.sixDigitCode')}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={loading}
                  style={{ border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '58px', borderRadius: '999px', opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? t('restaurantAuth.verifying') : t('restaurantAuth.confirm')}
                </button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0}
                    style={{ border: 'none', background: 'transparent', cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', color: '#054625', fontSize: '14px', fontWeight: 600, opacity: resendCooldown > 0 ? 0.4 : 1 }}
                  >
                    {resendCooldown > 0 ? t('restaurantAuth.resendIn', { seconds: resendCooldown }) : t('restaurantAuth.resendCode')}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => { setShow2FA(false); setOtpCode(''); setError(''); }}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'rgba(5,16,4,0.6)', fontSize: '14px', textAlign: 'center', padding: '4px' }}
                >
                  {t('restaurantAuth.backToLogin')}
                </button>
              </form>
            </div>
          )}
        </div>

        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.58)' }}>© 2026 namke</span>
      </div>

      {/* Aside */}
      <aside style={{ position: 'relative', background: '#054625', color: '#FBF9D9', minHeight: '420px', padding: 'clamp(24px, 3.5vw, 52px)', display: 'flex', flexDirection: 'column', gap: '20px', justifyContent: 'center', overflow: 'hidden' }} className="hidden lg:flex">
        <span style={{ position: 'absolute', right: '-120px', top: '-140px', width: '360px', height: '360px', borderRadius: '999px', background: '#72B744', opacity: 0.24 }} />
        <div className="animate-fade-in" style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '440px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFC717' }}>Espace cuisinier</span>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 40px)', lineHeight: 1.04, letterSpacing: '-0.025em' }}>
            {t('restaurantAuth.heroTitle')}
          </h2>
          <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.6, color: 'rgba(251,249,217,0.84)' }}>
            {t('restaurantAuth.heroSubtitle')}
          </p>
        </div>
        <ul className="animate-fade-in-delay" style={{ position: 'relative', margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '440px' }}>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Commission 12 %, versements chaque lundi</li>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Publiez vos plats du jour en quelques minutes</li>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Suivez vos commandes et vos avis en temps réel</li>
        </ul>
      </aside>
    </div>
  );
}
